import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, Loader2, ShieldAlert, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = {
  gecerli: { label: 'ONAYLI', note: 'Bu çalışma izni onaylıdır ve geçerlilik süresi içindedir. Girişe izin verilebilir.', cls: 'bg-emerald-600', icon: CheckCircle2 },
  baslamadi: { label: 'HENÜZ BAŞLAMADI', note: 'İzin onaylı ancak çalışma tarihi henüz gelmedi.', cls: 'bg-amber-500', icon: Clock },
  suresi_doldu: { label: 'SÜRESİ DOLDU', note: 'Bu iznin süresi dolmuştur. Girişe izin verilmemelidir.', cls: 'bg-rose-600', icon: XCircle },
  gecersiz: { label: 'GEÇERSİZ', note: 'Bu talep onaylı değil veya iptal edilmiş. Girişe izin verilmemelidir.', cls: 'bg-rose-600', icon: ShieldAlert },
};
const fmt = (d) => (d ? d.split('-').reverse().join('/') : '-');

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-0">
    <span className="text-slate-500">{label}</span><span className="text-right font-semibold text-slate-800">{value || '-'}</span>
  </div>
);

// Güvenlik girişinde okutulan QR kodun açtığı herkese açık doğrulama sayfası
export default function VerifyPermitPage() {
  const { permitId } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/permit-verify/${permitId}`, { params: { t: params.get('t') } })
      .then(res => setData(res.data)).catch(err => setError(err.response?.data?.detail || 'Doğrulama yapılamadı'));
  }, [permitId, params]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6"><div className="rounded-2xl bg-white p-8 text-center shadow" data-testid="verify-error"><ShieldAlert className="mx-auto mb-3 h-12 w-12 text-rose-600" /><p className="text-lg font-bold text-slate-900">Doğrulanamadı</p><p className="mt-1 text-sm text-slate-500">{error}</p></div></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-100"><Loader2 className="h-8 w-8 animate-spin text-slate-500" /></div>;
  const s = STATES[data.state] || STATES.gecersiz;
  const Icon = s.icon;
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6">
      <div className="mx-auto max-w-md overflow-hidden rounded-2xl bg-white shadow-lg" data-testid="verify-card">
        <div className={`${s.cls} px-6 py-7 text-center text-white`} data-testid={`verify-state-${data.state}`}>
          <Icon className="mx-auto mb-2 h-16 w-16" />
          <p className="text-2xl font-extrabold tracking-wide">{s.label}</p>
          <p className="mt-1 text-sm opacity-90">{s.note}</p>
        </div>
        <div className="px-6 py-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Çalışma İzni #{data.request_number}</p>
          <Row label="Firma" value={data.company_name} />
          <Row label="Lokasyon" value={data.location_name} />
          <Row label="Tarih" value={`${fmt(data.start_date)} – ${fmt(data.end_date)}`} />
          <Row label="Saat" value={`${data.start_time || '-'} – ${data.end_time || '-'}`} />
          <Row label="İş Tanımı" value={data.job_description} />
        </div>
        <div className="border-t bg-slate-50 px-6 py-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">İzinli Personel ({data.employees.length})</p>
          <ul className="space-y-2" data-testid="verify-employees">
            {data.employees.map((e, i) => (
              <li key={i} className="rounded-lg bg-white px-3 py-2 text-sm shadow-sm" data-testid={`verify-employee-${i}`}>
                <Row label="Firması" value={e.company} />
                <Row label="Adı" value={e.first_name} />
                <Row label="Soyadı" value={e.last_name} />
                <Row label="T.C." value={e.tc} />
                <Row label="Görevi" value={e.position} />
              </li>
            ))}
          </ul>
          {data.equipments.length > 0 && <p className="mt-3 text-sm text-slate-600"><b>Ekipman:</b> {data.equipments.join(', ')}</p>}
        </div>
        <p className="px-6 py-3 text-center text-xs text-slate-400">Kontrol zamanı: {data.checked_at}</p>
      </div>
    </div>
  );
}
